import type { GtKeyword, GtTurn } from '@/api/internal'

export interface TextRange {
  start: number
  end: number
}

export interface KeywordRange extends TextRange {
  keyword: GtKeyword
}

export interface BlockTail extends TextRange {
  complete: boolean
}

export interface StopCursor {
  turnIndex: number
  offset: number
}

export interface TextSegment extends TextRange {
  text: string
  keyword?: GtKeyword
  inTail: boolean
  played: boolean
  focused: boolean
}

export interface SegmentedText {
  segments: TextSegment[]
  caretOffset: number | null
}

const WORD_CHARACTER = new RegExp('[\\p{L}\\p{N}]', 'u')
const CLOSING_PUNCTUATION = /["'”’)\]]+$/
const SENTENCE_BOUNDARY = /[.?!…]["'”’)\]]*\s+/g

const previousCodepoint = (text: string, offset: number): string | undefined => Array.from(text.slice(0, offset)).at(-1)

const nextCodepoint = (text: string, offset: number): string | undefined => Array.from(text.slice(offset))[0]

export const keywordRanges = (text: string, keywords: readonly GtKeyword[]): KeywordRange[] => {
  const candidates: KeywordRange[] = []
  for (const keyword of keywords) {
    if (!keyword.phrase) continue
    let from = 0
    while (from <= text.length - keyword.phrase.length) {
      const start = text.indexOf(keyword.phrase, from)
      if (start < 0) break
      const end = start + keyword.phrase.length
      const before = previousCodepoint(text, start)
      const after = nextCodepoint(text, end)
      if ((!before || !WORD_CHARACTER.test(before)) && (!after || !WORD_CHARACTER.test(after))) {
        candidates.push({ start, end, keyword })
      }
      from = start + 1
    }
  }
  candidates.sort((left, right) => left.start - right.start || right.end - left.end)

  const ranges: KeywordRange[] = []
  for (const candidate of candidates) {
    const previous = ranges.at(-1)
    if (previous && candidate.start < previous.end) continue
    ranges.push(candidate)
  }
  return ranges
}

export const blockTail = (text: string): BlockTail | null => {
  const end = text.trimEnd().length
  if (end === 0) return null

  const trimmed = text.slice(0, end)
  SENTENCE_BOUNDARY.lastIndex = 0
  let sentenceStart = 0
  let hasEarlierSentence = false
  let match = SENTENCE_BOUNDARY.exec(trimmed)
  while (match) {
    sentenceStart = match.index + match[0].length
    hasEarlierSentence = true
    match = SENTENCE_BOUNDARY.exec(trimmed)
  }

  const lastSentence = trimmed.slice(sentenceStart)
  const withoutClosing = lastSentence.replace(CLOSING_PUNCTUATION, '')
  const complete = /[.?!…]$/.test(withoutClosing)
  const words: RegExpExecArray[] = []
  const wordMatcher = /\S+/g
  let word = wordMatcher.exec(lastSentence)
  while (word) {
    words.push(word)
    word = wordMatcher.exec(lastSentence)
  }

  if (complete) {
    if (!hasEarlierSentence || words.length > 4) return null
    return { start: sentenceStart, end, complete: true }
  }

  if (words.length === 0) return null
  const start = words.length > 8 ? sentenceStart + (words.at(-8)?.index ?? 0) : sentenceStart
  return { start, end, complete: false }
}

export const turnStopOffsets = (text: string, keywords: readonly GtKeyword[]): number[] => {
  const tail = blockTail(text)
  return Array.from(
    new Set([0, ...keywordRanges(text, keywords).map(({ start }) => start), ...(tail ? [tail.start] : [])])
  ).sort((left, right) => left - right)
}

export const nextStop = (
  stopsByTurnIndex: readonly (readonly number[])[],
  cursor: StopCursor,
  direction: 1 | -1
): StopCursor | null => {
  if (direction === 1) {
    for (let turnIndex = Math.max(0, cursor.turnIndex); turnIndex < stopsByTurnIndex.length; turnIndex += 1) {
      for (const offset of stopsByTurnIndex[turnIndex]) {
        if (turnIndex > cursor.turnIndex || offset > cursor.offset) return { turnIndex, offset }
      }
    }
    return null
  }

  for (let turnIndex = Math.min(cursor.turnIndex, stopsByTurnIndex.length - 1); turnIndex >= 0; turnIndex -= 1) {
    const stops = stopsByTurnIndex[turnIndex]
    for (let index = stops.length - 1; index >= 0; index -= 1) {
      const offset = stops[index]
      if (turnIndex < cursor.turnIndex || offset < cursor.offset) return { turnIndex, offset }
    }
  }
  return null
}

export const offsetToMs = (turn: GtTurn, text: string, offset: number): number => {
  if (turn.end_ms <= turn.start_ms) return turn.start_ms
  const codepoints = Array.from(text)
  if (codepoints.length === 0) return turn.start_ms
  const clampedOffset = Math.max(0, Math.min(text.length, offset))
  const codepointOffset = Array.from(text.slice(0, clampedOffset)).length
  return turn.start_ms + Math.round(((turn.end_ms - turn.start_ms) * codepointOffset) / codepoints.length)
}

export const segmentText = (
  text: string,
  ranges: readonly KeywordRange[],
  tail: BlockTail | null,
  playheadOffset: number | null,
  focusedOffset: number | null = null
): SegmentedText => {
  const caretOffset = playheadOffset == null ? null : Math.max(0, Math.min(text.length, playheadOffset))
  const focused = focusedOffset == null ? null : Math.max(0, Math.min(text.length, focusedOffset))
  const points = new Set<number>([0, text.length])
  for (const range of ranges) {
    points.add(range.start)
    points.add(range.end)
  }
  if (tail) {
    points.add(tail.start)
    points.add(tail.end)
  }
  if (caretOffset != null) points.add(caretOffset)
  if (focused != null) points.add(focused)
  const boundaries = Array.from(points).sort((left, right) => left - right)
  const segments: TextSegment[] = []

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const start = boundaries[index]
    const end = boundaries[index + 1]
    if (start === end) continue
    const range = ranges.find((candidate) => candidate.start <= start && candidate.end >= end)
    segments.push({
      start,
      end,
      text: text.slice(start, end),
      ...(range ? { keyword: range.keyword } : {}),
      inTail: tail != null && tail.start <= start && tail.end >= end,
      played: caretOffset != null && end <= caretOffset,
      focused: focused != null && start === focused,
    })
  }

  return { segments, caretOffset }
}
